import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, Star as StarIcon } from "lucide-react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";

export default function AdminPackages() {
  const [list, setList] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pkgs, dests] = await Promise.all([
        adminApi.get("/packages/admin/all"),
        adminApi.get("/destinations/admin/all"),
      ]);
      setList(pkgs.data);
      setDestinations(dests.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePublish = async (pkg) => {
    try {
      await adminApi.put(`/packages/${pkg.id}`, { published: !pkg.published });
      setList((l) => l.map((p) => (p.id === pkg.id ? { ...p, published: !p.published } : p)));
      toast.success(pkg.published ? "Unpublished" : "Published");
    } catch { toast.error("Failed to update"); }
  };

  const remove = async (pkg) => {
    if (!window.confirm(`Delete "${pkg.title}"?`)) return;
    try {
      await adminApi.delete(`/packages/${pkg.id}`);
      setList((l) => l.filter((p) => p.id !== pkg.id));
      toast.success("Package deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const startNew = () => setEditing({
    title: "", slug: "", destination_slug: destinations[0]?.slug || "", category: "inbound",
    type: "standard", tagline: "", short: "", price_from: "", nights: "", hero: "",
    overview: "", highlights: "", inclusions: "", exclusions: "", gallery: "",
    published: true, featured: false, sort_order: 0,
  });

  const startEdit = (pkg) => setEditing({
    ...pkg,
    highlights: (pkg.highlights || []).join("\n"),
    inclusions: (pkg.inclusions || []).join("\n"),
    exclusions: (pkg.exclusions || []).join("\n"),
    gallery: (pkg.gallery || []).join("\n"),
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...editing,
        highlights: editing.highlights ? editing.highlights.split("\n").map((s) => s.trim()).filter(Boolean) : [],
        inclusions: editing.inclusions ? editing.inclusions.split("\n").map((s) => s.trim()).filter(Boolean) : [],
        exclusions: editing.exclusions ? editing.exclusions.split("\n").map((s) => s.trim()).filter(Boolean) : [],
        gallery: editing.gallery ? editing.gallery.split("\n").map((s) => s.trim()).filter(Boolean) : [],
      };
      if (editing.id) {
        await adminApi.put(`/packages/${editing.id}`, payload);
        toast.success("Package saved");
      } else {
        await adminApi.post("/packages", payload);
        toast.success("Package created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));
  const inputCls = "w-full bg-transparent border border-border focus:border-accent transition-colors px-4 py-3 outline-none text-sm";
  const labelCls = "eyebrow block mb-2";

  if (editing) {
    return (
      <form onSubmit={save} className="space-y-8 max-w-5xl" data-testid="admin-package-form">
        <div className="flex items-center justify-between">
          <button type="button" onClick={() => setEditing(null)} className="text-[11px] uppercase tracking-[0.25em] text-foreground/60 hover:text-accent">
            ← Back to packages
          </button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-colors uppercase text-[11px] tracking-[0.25em] disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl">{editing.id ? editing.title : "New package."}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Title</label><input required value={editing.title} onChange={set("title")} className={inputCls} /></div>
              <div><label className={labelCls}>Slug</label><input value={editing.slug} onChange={set("slug")} className={inputCls} placeholder="auto" /></div>
            </div>
            <div><label className={labelCls}>Tagline</label><input value={editing.tagline} onChange={set("tagline")} className={inputCls} /></div>
            <div><label className={labelCls}>Short description</label><textarea rows={2} value={editing.short} onChange={set("short")} className={`${inputCls} resize-none`} /></div>
            <div><label className={labelCls}>Overview</label><textarea rows={5} value={editing.overview} onChange={set("overview")} className={`${inputCls} resize-y`} /></div>
            <div><label className={labelCls}>Highlights (one per line)</label><textarea rows={5} value={editing.highlights} onChange={set("highlights")} className={`${inputCls} resize-y`} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Inclusions (one per line)</label><textarea rows={5} value={editing.inclusions} onChange={set("inclusions")} className={`${inputCls} resize-y`} /></div>
              <div><label className={labelCls}>Exclusions (one per line)</label><textarea rows={5} value={editing.exclusions} onChange={set("exclusions")} className={`${inputCls} resize-y`} /></div>
            </div>
          </div>
          <div className="space-y-6">
            <div>
              <label className={labelCls}>Destination</label>
              <select value={editing.destination_slug} onChange={set("destination_slug")} className={inputCls}>
                {destinations.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select value={editing.category} onChange={set("category")} className={inputCls}>
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Type</label>
              <select value={editing.type} onChange={set("type")} className={inputCls}>
                <option value="standard">Standard</option>
                <option value="honeymoon">Honeymoon</option>
                <option value="adventure">Adventure</option>
                <option value="wellness">Wellness</option>
                <option value="family">Family</option>
              </select>
            </div>
            <div><label className={labelCls}>Price from</label><input value={editing.price_from} onChange={set("price_from")} className={inputCls} /></div>
            <div><label className={labelCls}>Nights</label><input value={editing.nights} onChange={set("nights")} className={inputCls} /></div>
            <div><label className={labelCls}>Hero image URL</label><input value={editing.hero} onChange={set("hero")} className={inputCls} /></div>
            {editing.hero && <img src={editing.hero} alt="" className="w-full aspect-video object-cover border border-border" />}
            <div><label className={labelCls}>Gallery URLs (one per line)</label><textarea rows={3} value={editing.gallery} onChange={set("gallery")} className={`${inputCls} resize-y font-mono text-xs`} /></div>
            <div><label className={labelCls}>Sort order</label><input type="number" value={editing.sort_order} onChange={set("sort_order")} className={inputCls} /></div>
            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked={!!editing.featured} onChange={set("featured")} className="accent-current" /> Featured
            </label>
            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked={!!editing.published} onChange={set("published")} className="accent-current" /> Published
            </label>
          </div>
        </div>
      </form>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-packages-list">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-3">Content</div>
          <h1 className="font-serif text-4xl md:text-5xl">Packages.</h1>
        </div>
        <button
          onClick={startNew}
          className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-colors uppercase text-[11px] tracking-[0.25em]"
        >
          <Plus size={14} strokeWidth={1.5} /> New Package
        </button>
      </div>

      {loading ? (
        <div className="text-foreground/50 text-center py-20">Loading…</div>
      ) : list.length === 0 ? (
        <div className="border border-border p-16 text-center text-foreground/60">No packages yet.</div>
      ) : (
        <ul className="space-y-3">
          {list.map((p) => (
            <li key={p.id} className="border border-border bg-background flex flex-col md:flex-row md:items-center gap-4 p-4 md:p-5">
              <div className="w-full md:w-24 aspect-[4/3] md:aspect-square overflow-hidden shrink-0 bg-secondary">
                {p.hero && <img src={p.hero} alt={p.title} className="h-full w-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="eyebrow text-foreground/55">{p.destination_slug}</span>
                  <span className="eyebrow text-accent">{p.type}</span>
                  {p.featured && <StarIcon size={12} className="text-accent fill-accent" />}
                  {!p.published && <span className="text-[9px] uppercase tracking-[0.22em] border border-border px-2 py-0.5 text-foreground/55">Draft</span>}
                </div>
                <div className="font-serif text-xl md:text-2xl leading-tight">{p.title}</div>
                <div className="text-[11px] text-foreground/55 mt-1">{p.nights} nights · From {p.price_from}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => togglePublish(p)} className="p-2.5 border border-border hover:border-foreground transition-colors">
                  {p.published ? <Eye size={14} strokeWidth={1.5} /> : <EyeOff size={14} strokeWidth={1.5} />}
                </button>
                <button onClick={() => startEdit(p)} className="p-2.5 border border-border hover:border-foreground transition-colors">
                  <Pencil size={14} strokeWidth={1.5} />
                </button>
                <button onClick={() => remove(p)} className="p-2.5 border border-border hover:border-destructive hover:text-destructive transition-colors">
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
